import { useMemo } from 'react';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { buildFeedQueryFilters, EVENTS_PAGE_SIZE } from '../../utils/eventFeedModel';
import { buildFeedRpcParams, fetchEventsFeedPage } from '../../api/events';
import { queryKeys } from '../../lib/query/queryKeys';
import { GC, STALE } from '../../lib/query/staleTimes';

export function useEventsFeed({
  sortBy,
  genreFilter,
  debouncedSearch,
  venueTierFilter,
  dateFilter,
  customRange,
  computeRpcMinDate,
  computeRpcMaxDate,
  userId,
  authReady,
}) {
  const filters = useMemo(
    () =>
      buildFeedQueryFilters({
        sortBy,
        genreFilter,
        debouncedSearch,
        venueTierFilter,
        dateFilter,
        customRange,
        userId,
      }),
    [sortBy, genreFilter, debouncedSearch, venueTierFilter, dateFilter, customRange, userId],
  );

  return useInfiniteQuery({
    queryKey: queryKeys.events.feed(filters),
    queryFn: async ({ pageParam = 0 }) => {
      const { rpcName, params } = buildFeedRpcParams({
        offset: pageParam,
        sortBy,
        genreFilter,
        debouncedSearch,
        venueTierFilter,
        computeRpcMinDate,
        computeRpcMaxDate,
        userId,
      });
      return fetchEventsFeedPage({ rpcName, params });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < EVENTS_PAGE_SIZE) return undefined;
      return allPages.length * EVENTS_PAGE_SIZE;
    },
    enabled: authReady,
    staleTime: STALE.feed,
    gcTime: GC.long,
    placeholderData: keepPreviousData,
  });
}

export function flattenFeedPages(data) {
  if (!data?.pages) return [];
  const seen = new Set();
  const out = [];
  for (const page of data.pages) {
    for (const row of page) {
      if (!seen.has(row.event_id)) {
        seen.add(row.event_id);
        out.push(row);
      }
    }
  }
  return out;
}
